import { useHandler } from "@redotech/react-util/hook";
import { FulfillmentOrderAddress } from "@redotech/redo-model/fulfillments/fulfillment-order-address";
import { createContext, memo, ReactNode, useState } from "react";
import { ChangeShippingAddressModal } from "../change-shipping-address-modal";
import { SelectShippingAddressModal } from "../select-shipping-address-modal";

export const EditAddressModalContext = createContext<
  (
    currentAddress: FulfillmentOrderAddress | null,
    updateCallback: (address: FulfillmentOrderAddress) => Promise<void>,
    onUseAutoCleanedAddress: (useAutoCleanedAddress: boolean) => void,
  ) => void
>(() => {});

export const EditAddressModalProvider = memo(function EditAddressModalProvider({
  children,
}: {
  children: ReactNode;
}) {
  const [currentAddress, setCurrentAddress] =
    useState<FulfillmentOrderAddress | null>(null);
  const [open, setOpen] = useState(false);
  const [showEditOriginalAddressModal, setShowEditOriginalAddressModal] =
    useState(false);
  const [onAddressUpdate, setOnAddressUpdate] = useState<
    ((address: FulfillmentOrderAddress) => Promise<void>) | undefined
  >();
  const [onUseAutoCleanedAddress, setOnUseAutoCleanedAddress] = useState<
    (useAutoCleanedAddress: boolean) => void
  >(() => {});

  const openModal = (
    address: FulfillmentOrderAddress | null,
    updateCallback: (address: FulfillmentOrderAddress) => Promise<void>,
    onUseAutoCleanedAddress: (useAutoCleanedAddress: boolean) => void,
  ) => {
    setCurrentAddress(address);
    setOnAddressUpdate(() => updateCallback);
    setOnUseAutoCleanedAddress(() => onUseAutoCleanedAddress);
    setOpen(true);
  };

  const onClose = useHandler(() => {
    setOpen(false);
    setCurrentAddress(null);
    setOnUseAutoCleanedAddress(() => {});
    setOnAddressUpdate(undefined);
    setShowEditOriginalAddressModal(false);
  });

  const handleSubmit = useHandler(async (address: FulfillmentOrderAddress) => {
    if (onAddressUpdate) {
      await onAddressUpdate(address);
    }
    setShowEditOriginalAddressModal(false);
    onClose();
  });

  const handleSelectShippingAddress = useHandler(
    (useAutoCleanedAddress: boolean) => {
      setShowEditOriginalAddressModal(false);
      onUseAutoCleanedAddress(useAutoCleanedAddress);
      onClose();
    },
  );

  const handleEditOriginalAddress = useHandler(() => {
    setShowEditOriginalAddressModal(true);
  });

  return (
    <EditAddressModalContext.Provider value={openModal}>
      {children}
      <>
        {showEditOriginalAddressModal ? (
          <ChangeShippingAddressModal
            address={currentAddress}
            onClose={onClose}
            onSubmit={handleSubmit}
            open={open}
          />
        ) : (
          <SelectShippingAddressModal
            onClose={onClose}
            onEditOriginalAddress={handleEditOriginalAddress}
            onSubmit={handleSelectShippingAddress}
            open={open}
          />
        )}
      </>
    </EditAddressModalContext.Provider>
  );
});
